import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import {
  BatteryInformationFeaturesType,
  BatteryInternalComponentsProperties,
  ConsolidatedFeature,
  ConsolidatorDevice,
  CumulatedEnergyProperties,
  EssBatteryNVersionFirmware,
  EssOperationStateProperties,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  IsActiveProperty,
  IsEnabledPropertyGeneric,
  IsNumberValueProperty,
  IsStringValueProperty,
} from '@vi-iot/client';

import { AuthStore, DevicesStore, FeatureService, InstallationFeaturesStore } from '@store';

import { take, takeUntil } from 'rxjs/operators';
import { FeaturesDetailsOverlayTypeEnum } from '../../installation-overlay.model';
import { NO_VALUE } from '@pipes';

@Component({
  selector: 'app-battery-overlay',
  templateUrl: './battery-overlay.component.html',
  styleUrls: ['./battery-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatteryOverlayComponent implements OnInit, OnDestroy {
  readonly featurePropertyKey = FeaturePropertyKey;
  availableOverlays = FeaturesDetailsOverlayTypeEnum;
  batteryOperationState$: Observable<Feature<EssOperationStateProperties, never>>;
  batteries$: Observable<Feature<IsEnabledPropertyGeneric<number>, never>>;
  batteryDischargeCumulated$: Observable<Feature<CumulatedEnergyProperties, never>>;
  power$: Observable<Feature<IsNumberValueProperty, unknown>>;
  powerConsolidated$: Observable<Feature<ConsolidatedFeature, unknown>>;
  powerConsolidatedFeatures$: Observable<Feature<ConsolidatedFeature, unknown>>;
  powerConsolidatedPerDevice$: Observable<ConsolidatorDevice>;
  stateOfCharge$: Observable<Feature<IsNumberValueProperty, unknown>>;
  stateOfChargeConsolidated$: Observable<Feature<ConsolidatedFeature, unknown>>;
  stateOfChargeConsolidatedFeatures$: Observable<Feature<ConsolidatedFeature, unknown>>;
  stateOfChargeConsolidatedPerDevice$: Observable<ConsolidatorDevice>;
  capacity$: Observable<Feature<ConsolidatedFeature, unknown>>;
  BatteryInformationFeaturesType = BatteryInformationFeaturesType;
  deviceId: string;
  stateOfChargeConsolidatedDeviceId: string;
  powerConsolidatedDeviceId: string;
  NO_VALUE = NO_VALUE;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  batteryCapacity$: Observable<Feature<IsNumberValueProperty, never>>;
  batteryCapacityConsolidated$: Observable<
    ConsolidatorDevice | Feature<ConsolidatedFeature, unknown>
  >;
  constructor(
    public devicesStore: DevicesStore,
    public authStore: AuthStore,
    public featureService: FeatureService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.featureService
      .getConsolidatedFeatures(FeatureNames.stateOfChargeConsolidated)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((feature) => {
        this.stateOfChargeConsolidatedDeviceId = feature?.deviceId;
      });
    this.featureService
      .getConsolidatedFeatures(FeatureNames.powerConsolidated)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((feature) => {
        this.powerConsolidatedDeviceId = feature?.deviceId;
      });
    this.devicesStore.getSelectedDevice$.pipe(take(1)).subscribe((richDevice) => {
      this.deviceId = richDevice.id;
    });

    this.batteryOperationState$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.batteryOperationState
    );

    this.batteries$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.batteries
    );
    this.batteryDischargeCumulated$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.batteryDischargeCumulated
    );
    this.power$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.power,
      this.deviceId
    );

    this.powerConsolidated$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.powerConsolidated,
      this.deviceId
    );
    this.powerConsolidatedFeatures$ = this.featureService.getConsolidatedFeatures(
      FeatureNames.powerConsolidated
    );
    this.powerConsolidatedPerDevice$ = this.installationFeaturesStore.selectConsolidatedValues(
      FeatureNames.powerConsolidated,
      this.deviceId,
      this.powerConsolidatedDeviceId
    );

    this.stateOfCharge$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.stateOfCharge,
      this.deviceId
    );

    this.stateOfChargeConsolidated$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.stateOfChargeConsolidated,
      this.deviceId
    );

    this.stateOfChargeConsolidatedFeatures$ = this.featureService.getConsolidatedFeatures(
      FeatureNames.stateOfChargeConsolidated
    );

    this.stateOfChargeConsolidatedPerDevice$ = this.installationFeaturesStore.selectConsolidatedValues(
      FeatureNames.stateOfChargeConsolidated,
      this.deviceId,
      this.stateOfChargeConsolidatedDeviceId
    );

    this.setBatteryCapacityFeature();
  }

  getBatteryFeature(batteryId: number): Observable<Feature<IsActiveProperty, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.battery(batteryId.toString())
    );
  }

  getBatteryInternalComponents(): Observable<Feature<BatteryInternalComponentsProperties, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.batteryInternalComponents
    );
  }

  getBatteryHardwareRevision(batteryId: number): Observable<Feature<IsStringValueProperty, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.batteryHardwareRevision(batteryId.toString())
    );
  }

  getBatteryFirmware(batteryId: number): Observable<Feature<EssBatteryNVersionFirmware, never>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.batteryFirmware(batteryId.toString())
    );
  }

  private setBatteryCapacityFeature(): void {
    this.devicesStore.shouldUseConsolidatedFeature$
      .pipe(take(1), takeUntil(this.ngUnsubscribe))
      .subscribe((shouldUseConsolidatedFeature) => {
        if (shouldUseConsolidatedFeature) {
          this.batteryCapacityConsolidated$ = this.featureService.getCapacityBatteryByDevicesId(
            FeatureNames.stateOfCapacityNominalConsolidated,
            this.deviceId
          );
        } else {
          this.batteryCapacity$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
            FeatureNames.stateOfCapacityNominal
          );
        }
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
